import { useEffect, useRef } from "react";

/**
 * Adaptive polling hook
 *
 * @param {Function} callback        async function to run (poll)
 * @param {Object} options
 * @param {boolean} options.enabled  master on/off switch
 * @param {number} options.fastMs    interval when active
 * @param {number} options.slowMs    interval when idle/hidden
 * @param {number} options.idleMs    idle timeout
 */
export default function useAdaptivePolling(
  callback,
  {
    enabled = true,
    fastMs = 2000,
    slowMs = 10000,
    idleMs = 60000,
  } = {}
) {
  const intervalRef = useRef(null);
  const lastActiveRef = useRef(Date.now());
  const callbackRef = useRef(callback);

  // Keep callback fresh
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Track user activity (idle detection)
  useEffect(() => {
    const markActive = () => {
      lastActiveRef.current = Date.now();
    };

    window.addEventListener("mousemove", markActive);
    window.addEventListener("keydown", markActive);
    window.addEventListener("click", markActive);
    window.addEventListener("scroll", markActive);

    return () => {
      window.removeEventListener("mousemove", markActive);
      window.removeEventListener("keydown", markActive);
      window.removeEventListener("click", markActive);
      window.removeEventListener("scroll", markActive);
    };
  }, []);

  // Main polling controller
  useEffect(() => {
    if (!enabled) return;

    const inFlightRef = { current: false };

    const runPoll = () => {
      if (inFlightRef.current) return;
      const result = callbackRef.current();
      if (result && typeof result.then === "function") {
        inFlightRef.current = true;
        result.finally(() => {
          inFlightRef.current = false;
        });
      }
    };

    const startPolling = (fireNow = false) => {
      clearInterval(intervalRef.current);

      const now = Date.now();
      const isIdle = now - lastActiveRef.current > idleMs;
      const isHidden = document.visibilityState !== "visible";

      const interval = isHidden || isIdle ? slowMs : fastMs;

      intervalRef.current = setInterval(runPoll, interval);
      if (fireNow) runPoll();
    };

    // Start immediately
    startPolling(true);

    // Visibility change → recompute interval
    const onVisibilityChange = () => startPolling(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisibilityChange);

    // Re-evaluate interval only (do not fire an extra overlapping poll)
    const reevaluate = setInterval(() => startPolling(false), 10000);

    return () => {
      clearInterval(intervalRef.current);
      clearInterval(reevaluate);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [enabled, fastMs, slowMs, idleMs]);
}
